import React, { useEffect, useState } from "react";
import { Gift, Store, Zap, CheckCircle2, AlertCircle } from "lucide-react";
import axiosAdmin from "../axiosAdmin"; 

export default function AdminPromotions() {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoInputs, setPromoInputs] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    try {
      const res = await axiosAdmin.get("/admin/merchants");
      
      // ✅ PROTECTION : On vérifie si c'est un tableau, sinon on cherche la clé "merchants"
      // Si ton backend renvoie { success: true, merchants: [...] }, cela fonctionnera.
      const data = Array.isArray(res.data) 
        ? res.data 
        : (res.data.merchants || []);

      setMerchants(data);
      
      const initialInputs = {};
      data.forEach(m => {
        initialInputs[m._id] = 0;
      });
      setPromoInputs(initialInputs);
    } catch (error) {
      console.error("Erreur lors de la récupération des marchands", error);
      setMerchants([]); // Évite que le .map plante plus bas
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (id, value) => {
    setPromoInputs(prev => ({ ...prev, [id]: parseInt(value) || 0 }));
  };

  const handleActivatePromo = async (merchantId) => {
    const ordersToAdd = promoInputs[merchantId];
    if (ordersToAdd <= 0) return;

    setSavingId(merchantId);
    try {
      await axiosAdmin.put(`/admin/merchants/${merchantId}/promo-fees`, {
        freeOrders: ordersToAdd
      });

      setMerchants(prev => prev.map(m => {
        if (m._id === merchantId) {
          return { ...m, freeServiceOrdersLeft: (m.freeServiceOrdersLeft || 0) + ordersToAdd };
        }
        return m;
      }));
      
      setPromoInputs(prev => ({ ...prev, [merchantId]: 0 }));
    } catch (error) {
      console.error("Erreur lors de l'activation de la promo", error);
    } finally {
      setSavingId(null);
    }
  };

  const handleDeactivatePromo = async (merchantId) => {
    setSavingId(merchantId);
    try {
      await axiosAdmin.put(`/admin/merchants/${merchantId}/promo-fees`, {
        freeOrders: 0 
      });

      setMerchants(prev => prev.map(m => 
        m._id === merchantId ? { ...m, freeServiceOrdersLeft: 0 } : m
      ));
    } catch (error) {
      console.error("Erreur", error);
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen font-bold text-slate-400 bg-[#FAFAFA]">
        <div className="animate-pulse">Chargement des promotions Souqi...</div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-[#FAFAFA] min-h-screen font-sans text-left">
      
      {/* HEADER */}
      <header className="relative w-full bg-gradient-to-r from-slate-900 to-slate-800 min-h-[10rem] rounded-[2.5rem] p-10 mb-10 shadow-xl overflow-hidden flex items-center justify-between">
        <div className="absolute top-0 right-0 w-64 h-64 -mt-20 -mr-10 bg-[#f34141] rounded-full pointer-events-none opacity-20 blur-3xl"></div>
        <div className="relative z-10 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Gift size={28} className="text-[#f34141]" />
            <h1 className="text-3xl font-black tracking-tight">Gestion des Promotions</h1>
          </div>
          <p className="text-sm font-medium text-slate-300">
            Offrez des commandes sans frais de service aux marchands de Nouakchott.
          </p>
        </div>
      </header>

      {/* LISTE DES MARCHANDS */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* ✅ PROTECTION : On s'assure que merchants est un tableau avant le .map */}
        {Array.isArray(merchants) && merchants.length > 0 ? (
          merchants.map((merchant) => {
            const hasActivePromo = merchant.freeServiceOrdersLeft > 0;

            return (
              <div key={merchant._id} className={`relative bg-white p-6 rounded-[2rem] border transition-all duration-300 shadow-sm hover:shadow-xl
                ${hasActivePromo ? 'border-[#f34141]/30 ring-4 ring-[#f34141]/5' : 'border-gray-100'}
              `}>
                {hasActivePromo && (
                  <div className="absolute flex items-center gap-1 px-3 py-1 text-[10px] font-black text-white uppercase bg-[#f34141] rounded-full shadow-md -top-3 -right-3 animate-pulse">
                    <Zap size={12} fill="currentColor" />
                    Promo Active
                  </div>
                )}

                <div className="flex items-center gap-4 mb-6">
                  <div className={`flex items-center justify-center w-12 h-12 rounded-2xl ${hasActivePromo ? 'bg-red-50 text-[#f34141]' : 'bg-slate-50 text-slate-400'}`}>
                    <Store size={24} />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <h3 className="text-lg font-black truncate text-slate-800">{merchant.name || "Boutique sans nom"}</h3>
                    {hasActivePromo ? (
                      <p className="text-xs font-bold text-[#f34141] flex items-center gap-1">
                        <CheckCircle2 size={12} />
                        {merchant.freeServiceOrdersLeft} commandes offertes
                      </p>
                    ) : (
                      <p className="text-xs font-medium text-slate-400 flex items-center gap-1">
                        <AlertCircle size={12} />
                        Frais normaux
                      </p>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <label className="block mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    Ajouter du crédit promo :
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={promoInputs[merchant._id] || ''}
                      onChange={(e) => handleInputChange(merchant._id, e.target.value)}
                      className="w-20 px-3 py-2 text-lg font-black text-center bg-gray-50 border-2 border-transparent focus:border-[#f34141] focus:bg-white focus:outline-none rounded-xl text-slate-800"
                    />
                    <button
                      onClick={() => handleActivatePromo(merchant._id)}
                      disabled={savingId === merchant._id || !promoInputs[merchant._id]}
                      className="flex-1 py-2.5 px-4 bg-slate-900 hover:bg-[#f34141] text-white font-bold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingId === merchant._id ? '...' : 'Appliquer'}
                    </button>
                  </div>
                  
                  {hasActivePromo && (
                    <button
                      onClick={() => handleDeactivatePromo(merchant._id)}
                      className="w-full mt-3 py-2 text-[10px] font-black text-red-500 bg-red-50 hover:bg-red-100 rounded-xl transition-all uppercase tracking-tighter"
                    >
                      Annuler la promotion
                    </button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="col-span-full text-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-slate-200">
             <p className="text-slate-400 font-bold">Aucun marchand trouvé pour le moment.</p>
          </div>
        )}
      </div>
    </div>
  );
}
